import {
  Link2, RefreshCw, ShoppingCart, BarChart3, Code2, Layout,
  Zap, Shield, FileText, Crown, Lock, Mail, Columns3, Terminal,
  Upload, Puzzle
} from "lucide-react";

const freeFeatures = [
  {
    icon: Link2,
    title: "Amazon Link Import",
    desc: "Paste URLs from amazon.com, amazon.in, or amazon.co.uk. ASIN extracted automatically.",
    span: "md:col-span-6",
  },
  {
    icon: RefreshCw,
    title: "Auto-Sync Every 8h",
    desc: "WP-Cron refreshes price, rating, and availability. Failed syncs fall back to cached data.",
    span: "md:col-span-6",
  },
  {
    icon: ShoppingCart,
    title: "Amazon-Style Cards",
    desc: "Responsive product cards with images, ratings, stock badges, and Buy on Amazon buttons.",
    span: "md:col-span-4",
  },
  {
    icon: BarChart3,
    title: "Price History",
    desc: "Track price changes over time with sparkline charts displayed on each product card.",
    span: "md:col-span-4",
  },
  {
    icon: Code2,
    title: "Shortcodes",
    desc: "[affiliate_product id=\"123\"] for single cards and [affiliate_grid] for grids.",
    span: "md:col-span-4",
  },
  {
    icon: Layout,
    title: "Gutenberg Block",
    desc: "Native block editor support. Select products from a dropdown and preview in the editor.",
    span: "md:col-span-4",
  },
  {
    icon: Zap,
    title: "Transient Caching",
    desc: "Product data cached using WordPress Transients API. Only stale data triggers requests.",
    span: "md:col-span-4",
  },
  {
    icon: Shield,
    title: "Rotating UA & Backoff",
    desc: "8 browser user-agents with exponential backoff retry to reduce blocking.",
    span: "md:col-span-4",
  },
];

const proFeatures = [
  {
    icon: Mail,
    title: "Price Drop Email Alerts",
    desc: "Get notified when product prices drop. Set threshold and receive email digest after every sync.",
  },
  {
    icon: Columns3,
    title: "Comparison Tables",
    desc: "Side-by-side product comparisons with auto-highlighted Best Value and dynamic sorting.",
  },
  {
    icon: Puzzle,
    title: "Elementor Widget",
    desc: "Drag-and-drop product cards and grids in Elementor page builder. No shortcodes needed.",
  },
  {
    icon: Terminal,
    title: "WP-CLI Commands",
    desc: "wp affpro sync, wp affpro import <url>, wp affpro csv-import — automate from the command line.",
  },
  {
    icon: Upload,
    title: "CSV Bulk Import",
    desc: "Upload a CSV with Amazon URLs to import 10s or 100s of products at once.",
  },
  {
    icon: BarChart3,
    title: "Click Analytics & SEO",
    desc: "Track affiliate clicks, CTR, top products. JSON-LD Product schema for better rankings.",
  },
];

export default function FeaturesSection() {
  return (
    <section
      id="features"
      data-testid="features-section"
      className="py-24 md:py-32 px-6 md:px-12 lg:px-24 bg-[#F9FAFB]"
    >
      <div className="max-w-7xl mx-auto">
        {/* Free Features */}
        <div className="text-center mb-16 animate-fade-in-up">
          <span className="inline-flex items-center gap-2 bg-[#FF7300]/10 text-[#FF7300] px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
            Free Version
          </span>
          <h2 className="font-display font-extrabold text-[#09090B] text-3xl sm:text-4xl tracking-tight mb-4">
            Powerful Out of the Box
          </h2>
          <p className="text-[#52525B] text-base md:text-lg max-w-xl mx-auto">
            Everything you need to start — up to 20 products, auto-sync, shortcodes, and Gutenberg blocks.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {freeFeatures.map((f, i) => {
            const Icon = f.icon;
            return (
              <div
                key={f.title}
                data-testid={`feature-card-${i}`}
                className={`${f.span} bg-white border border-[#E4E4E7] rounded-lg p-6 hover:border-[#09090B] hover:shadow-md transition-all duration-300 group animate-fade-in-up`}
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div className="w-10 h-10 rounded-md bg-[#09090B] flex items-center justify-center mb-4 group-hover:bg-[#FF7300] transition-colors">
                  <Icon size={18} className="text-white" />
                </div>
                <h3 className="font-display font-bold text-[#09090B] text-base mb-2">
                  {f.title}
                </h3>
                <p className="text-[#52525B] text-sm leading-relaxed">
                  {f.desc}
                </p>
              </div>
            );
          })}
        </div>

        {/* Free limit badge */}
        <div className="flex justify-center mt-10 animate-fade-in-up delay-500">
          <div className="inline-flex items-center gap-3 bg-white border border-[#E4E4E7] rounded-full px-5 py-2.5">
            <Lock size={14} className="text-[#A1A1AA]" />
            <span className="text-sm text-[#52525B]">
              Free version: Up to <strong>20 products</strong> &middot; No API required
            </span>
          </div>
        </div>

        {/* PRO Features */}
        <div className="mt-24 text-center mb-12 animate-fade-in-up">
          <span className="inline-flex items-center gap-2 bg-[#09090B] text-white px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
            <Crown size={12} /> PRO Only
          </span>
          <h2 className="font-display font-extrabold text-[#09090B] text-3xl sm:text-4xl tracking-tight mb-4">
            Unlock Full Power
          </h2>
          <p className="text-[#52525B] text-base md:text-lg max-w-xl mx-auto">
            Unlimited products, advanced analytics, email alerts, Elementor integration, CLI tools, and more.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {proFeatures.map((f, i) => {
            const Icon = f.icon;
            return (
              <div
                key={f.title}
                data-testid={`pro-feature-card-${i}`}
                className="bg-[#09090B] border border-[#27272A] rounded-lg p-6 hover:border-[#FF7300] transition-all duration-300 group animate-fade-in-up"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-md bg-[#FF7300] flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Icon size={18} className="text-white" />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[#FF7300] bg-[#FF7300]/10 px-2 py-0.5 rounded-full">
                    PRO
                  </span>
                </div>
                <h3 className="font-display font-bold text-white text-base mb-2">
                  {f.title}
                </h3>
                <p className="text-[#A1A1AA] text-sm leading-relaxed">
                  {f.desc}
                </p>
              </div>
            );
          })}
        </div>

        {/* No API badge */}
        <div className="flex justify-center mt-12 animate-fade-in-up delay-500">
          <div className="inline-flex items-center gap-3 bg-white border border-[#E4E4E7] rounded-full px-5 py-2.5">
            <FileText size={16} className="text-[#52525B]" />
            <span className="text-sm text-[#52525B]">
              No Amazon API required — works entirely through page scraping
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
